import {
	IDrawerDataListColumn,
	Id,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataFieldIndexResource } from "../../../shared/types/dataField";
import { PermissionName } from "../../../shared/types/permission";
import { birthdayWidgetService } from "../dashboard/widgets/birthdays/service";
import { contactAttributeKeys } from "../shared/api/contactAttributeService";
import { contactDatabaseSettingsService } from "../shared/api/contactDatabaseSettingsService";
import { contactFilterService } from "../shared/api/contactFilterService";
import { contactService } from "../shared/api/contactService";
import { contactViewService } from "../shared/api/contactViewService";
import { dataFieldService } from "../shared/api/dataFieldService";
import { DataFieldSaveRequest } from "../shared/types/dataField";
import { hasDataFieldOptions } from "../shared/utils/dataField";
import { DataFieldCreateDrawer, DataFieldUpdateDrawer } from "./DataFieldDrawer";
import { DataFieldListContextMenu } from "./DataFieldListContextMenu";
import { DataFieldOptionListDrawer } from "./DataFieldOptionListDrawer";
import { useEditDrawerWithLocale } from "./useEditDrawerWithLocale";

export const DataFieldList = () => {
	const { t } = useTranslation();
	const canCreate = useHasPermission<PermissionName>(["dataField.create"]);
	const canUpdate = useHasPermission<PermissionName>(["dataField.update"]);

	const crudOperations = useLaravelCrudEntity(
		dataFieldService,
		{
			entityName: t("Data field"),
			messages: {
				create: t("Data field was saved."),
				update: t("Data field was saved."),
				delete: t("Data field was deleted."),
			},
		},
		[
			dataFieldService.optionsKey,
			dataFieldService.indexAllKey,
			contactAttributeKeys.index,
			contactViewService.indexKey,
			contactFilterService.indexKey,
			birthdayWidgetService.key,
		],
		[dataFieldService.indexAllKey, contactViewService.indexKey, contactService.indexKey],
	);
	const optionListDrawerProps = useEditDrawerWithLocale();
	const queryClient = useQueryClient();
	const dataListColumns: IDrawerDataListColumn<DataFieldIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Label"),
				valueKey: "label",
				type: "value",
			},
			{
				heading: t("Type"),
				valueKey: "type",
				type: "value",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: (dataField, openDrawer) => (
					<DataFieldListContextMenu
						dataField={dataField}
						handleEditOption={optionListDrawerProps.handleOpen}
						handleEdit={openDrawer}
						handleDelete={crudOperations.deleteEntity}
					/>
				),
			},
		],
		[crudOperations.deleteEntity, optionListDrawerProps.handleOpen, t],
	);

	const createDataField = useCallback(
		async (dataField: DataFieldSaveRequest) => {
			const dataFieldResponse = await crudOperations.createEntity(dataField);

			if (dataFieldResponse && hasDataFieldOptions(dataField.type)) {
				await queryClient.resetQueries({
					queryKey: contactDatabaseSettingsService.keys.getDataFieldOptions,
				});
				optionListDrawerProps.handleOpen(dataFieldResponse);
			}

			return dataFieldResponse;
		},
		[queryClient, crudOperations, optionListDrawerProps],
	);
	const updateDataField = useCallback(
		async (id: Id, dataField: DataFieldSaveRequest) => {
			const response = await crudOperations.updateEntity(id, {
				...dataField,
				translations: dataField.translations,
			});
			if (response) {
				await queryClient.resetQueries({ queryKey: contactDatabaseSettingsService.keys.getDataFieldOptions });
				await queryClient.resetQueries({ queryKey: contactDatabaseSettingsService.keys.getSettings });
			}
			return response;
		},
		[queryClient, crudOperations],
	);

	return (
		<>
			<PaginatedDrawerAutoDataList
				canCreate={canCreate}
				canUpdate={canUpdate}
				columns={dataListColumns}
				listFetchConfig={laravelPaginatedListFetchConfig(dataFieldService.listKey, dataFieldService.index)}
				initialSearch={{
					search: "",
					searchFields: [
						{ label: t("Name"), name: "name" },
						{ label: t("Label"), name: "label" },
						{ label: t("Placeholder"), name: "placeholder" },
					],
				}}
				renderCreateDrawer={(drawerProps) => (
					<DataFieldCreateDrawer {...drawerProps} onSubmit={(entityFormState) => createDataField(entityFormState)} />
				)}
				renderUpdateDrawer={(drawerProps) => (
					<DataFieldUpdateDrawer
						{...drawerProps}
						onOpenOptions={(locale) => optionListDrawerProps.handleOpen(drawerProps.id, locale)}
						onSubmit={(entityFormState) => updateDataField(drawerProps.id, entityFormState)}
					/>
				)}
				translations={{
					title: t("Data fields"),
					empty: t("No data field available."),
				}}
			/>
			{optionListDrawerProps.editEntityId && (
				<DataFieldOptionListDrawer
					dataFieldId={optionListDrawerProps.editEntityId}
					initialLocale={optionListDrawerProps.editLocale}
					{...optionListDrawerProps}
				/>
			)}
		</>
	);
};
